import type { FlowQuizSummary } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api/_client";

/**
 * QL-13 — a quiz is authored inside a `quiz` flow component, so "the quizzes
 * you can reference" is always a question about one flow. The id is the
 * component's id: what `__quiz_result_<id>` rules and `{{Quiz.<id>}}`
 * placeholders name. The shape lives in `@repo/shared/types` and is imported
 * directly by every consumer.
 */

const keys = {
  byFlow: (flowId: string | null) => ["flows", flowId, "quizzes"] as const,
  byTemplate: (templateId: string | null) => ["pdf-templates", templateId, "quizzes"] as const,
};

/**
 * The quizzes on a flow — for the flow editor's conditional-rule picker.
 * `isUnknown` is true while the list is loading or after its fetch failed:
 * `quizzes` is then an empty placeholder, which must not read as "every quiz
 * rule is dangling", so condition checks pass `null` instead.
 */
export function useFlowQuizzes(flowId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: keys.byFlow(flowId ?? null),
    queryFn: () => get<FlowQuizSummary[]>(`/internal/flows/${flowId}/quizzes`),
    enabled: !!flowId,
  });
  return { quizzes: data ?? [], isUnknown: data === undefined };
}

/**
 * The quizzes on the flow a PDF template is bound to — for the template's
 * conditional-rule pickers and Insert Field menus. `isUnknown` as above.
 */
export function usePdfTemplateQuizzes(templateId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: keys.byTemplate(templateId ?? null),
    queryFn: () => get<FlowQuizSummary[]>(`/internal/pdf-templates/${templateId}/quizzes`),
    enabled: !!templateId,
  });
  return { quizzes: data ?? [], isUnknown: data === undefined };
}
